import { retryBusy } from "../nest/appDataRetry"
import { requestNestMethod, type NestMethodParams, type NestMethodResult } from "../nest/client"
import {
    createScratchpadContentRecord,
    createScratchpadContentStore,
    createScratchpadIndexRecord,
    createScratchpadIndexStore,
    parseScratchpadContentRecord,
    parseScratchpadIndexRecord,
    scratchpadContentKey,
    scratchpadIndexKey,
    SCRATCHPADS_NAMESPACE,
    type ScratchpadContent,
    type ScratchpadContentStore,
    type ScratchpadIndexStore,
    type ScratchpadMeta,
} from "./scratchpadStore"

type ScratchpadMethod = "appData.get" | "appData.set" | "appData.delete"
export type ScratchpadRequest = <M extends ScratchpadMethod>(method: M, params: NestMethodParams<M>) => Promise<NestMethodResult<M>>

export interface ScratchpadIndexConnection {
    store: ScratchpadIndexStore
    sync: () => Promise<void>
    disconnect: () => void
}

export interface ScratchpadContentConnection {
    store: ScratchpadContentStore
    sync: () => Promise<void>
    disconnect: () => void
}

interface ScratchpadConnectionOptions {
    request?: ScratchpadRequest
    now?: () => string
}

const defaultRequest: ScratchpadRequest = (method, params) => requestNestMethod(method, params)

export async function connectScratchpadIndex(workspaceId: string, options: ScratchpadConnectionOptions = {}): Promise<ScratchpadIndexConnection> {
    const request = options.request ?? defaultRequest
    const now = options.now ?? (() => new Date().toISOString())
    const key = scratchpadIndexKey(workspaceId)
    let disposed = false
    let latestPads: ScratchpadMeta[] = []

    const result = await retryBusy(() =>
        request("appData.get", {
            namespace: SCRATCHPADS_NAMESPACE,
            key,
            scope: { workspaceId },
        })
    )
    const parsed = result.found ? parseScratchpadIndexRecord(result.value) : null
    latestPads = parsed?.pads ?? []

    const store = createScratchpadIndexStore(parsed ?? createScratchpadIndexRecord(workspaceId, [], now()), (pads) => {
        latestPads = pads
        if (!disposed) void sync()
    })

    async function sync(): Promise<void> {
        await retryBusy(() =>
            request("appData.set", {
                namespace: SCRATCHPADS_NAMESPACE,
                key,
                scope: { workspaceId },
                value: createScratchpadIndexRecord(workspaceId, latestPads, now()),
            })
        )
    }

    return {
        store,
        sync,
        disconnect: () => {
            disposed = true
        },
    }
}

export async function connectScratchpadContent(
    workspaceId: string,
    padId: string,
    options: ScratchpadConnectionOptions = {}
): Promise<ScratchpadContentConnection> {
    const request = options.request ?? defaultRequest
    const now = options.now ?? (() => new Date().toISOString())
    const key = scratchpadContentKey(padId)
    let disposed = false
    let latestContent: ScratchpadContent = { content: null, plainText: "" }

    const result = await retryBusy(() =>
        request("appData.get", {
            namespace: SCRATCHPADS_NAMESPACE,
            key,
            scope: { workspaceId },
        })
    )
    const parsed = result.found ? parseScratchpadContentRecord(result.value) : null
    latestContent = parsed ? { content: parsed.content, plainText: parsed.plainText } : latestContent

    const store = createScratchpadContentStore(parsed ?? createScratchpadContentRecord(workspaceId, padId, latestContent, now()), (content) => {
        latestContent = content
        if (!disposed) void sync()
    })

    async function sync(): Promise<void> {
        await retryBusy(() =>
            request("appData.set", {
                namespace: SCRATCHPADS_NAMESPACE,
                key,
                scope: { workspaceId },
                value: createScratchpadContentRecord(workspaceId, padId, latestContent, now()),
            })
        )
    }

    return {
        store,
        sync,
        disconnect: () => {
            disposed = true
        },
    }
}

export async function deleteScratchpadContent(workspaceId: string, padId: string, options: ScratchpadConnectionOptions = {}): Promise<void> {
    const request = options.request ?? defaultRequest
    await retryBusy(() =>
        request("appData.delete", {
            namespace: SCRATCHPADS_NAMESPACE,
            key: scratchpadContentKey(padId),
            scope: { workspaceId },
        })
    )
}
